/**
 * @file pages/CrmSettingsPage.js
 * @description Customer Butler — CRM Setup. Two things, self-serve (ADR-7 + ADR-8):
 *   1. Industry packs — pick your field (septic, …) and apply it (seeds the
 *      tailored custom fields + pipeline). This is "enter your field and BAM".
 *   2. Custom fields — add / edit / remove any field on any record type, Zoho-style.
 * Requires the customer:manage permission (admins).
 */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { Modal } from '../design-system';
import { apiGet, apiSend } from '../lib/api';
import { clearTagSuggestionCache } from '../components/TagEditor';

const ENTITY_TYPES = ['Customer', 'Contact', 'Property', 'Agent', 'Opportunity'];
const DATA_TYPES = ['text', 'textarea', 'number', 'decimal', 'currency', 'date', 'datetime',
  'boolean', 'dropdown', 'multiselect', 'email', 'phone', 'url', 'lookup', 'formula', 'file'];
const TYPES_WITH_OPTIONS = ['dropdown', 'multiselect'];

function CrmSettingsPage() {
  const auth = useContext(AuthContext);
  const { theme } = useTheme();
  const [packs, setPacks] = useState([]);
  const [entity, setEntity] = useState('Customer');
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // field def being edited, or {} for new
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadFields = useCallback(async (et) => {
    setLoading(true);
    try { setFields(await apiGet(`/customer/field-definitions?entityType=${et}`)); }
    catch (e) { setFields([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { apiGet('/customer/industry-packs').then(setPacks).catch(() => setPacks([])); }, []);
  useEffect(() => { loadFields(entity); }, [entity, loadFields]);

  if (!auth.isAuthenticated) return <Navigate to="/login" />;

  async function applyPack(key) {
    if (!window.confirm(`Apply the "${key}" pack? This adds its custom fields to your records.`)) return;
    setBusy(true); setMsg(null);
    const r = await apiSend(`/customer/industry-packs/${key}/apply`, 'POST', { packKey: key });
    setBusy(false);
    setMsg(r.ok ? `Applied "${key}" — ${r.data.created} new fields, ${r.data.updated} updated.` : (r.error?.message || 'Apply failed'));
    loadFields(entity);
  }

  async function saveField(form) {
    setBusy(true); setMsg(null);
    const body = {
      label: form.label, dataType: form.dataType, required: form.required,
      section: form.section || null, validation: form.validation || {},
      options: TYPES_WITH_OPTIONS.includes(form.dataType)
        ? { values: String(form.optionsText || '').split(/[\n,]/).map(s => s.trim()).filter(Boolean) }
        : {},
    };
    let r;
    if (form.id) {
      r = await apiSend(`/customer/field-definitions/${form.id}`, 'PUT', body);
    } else {
      r = await apiSend('/customer/field-definitions', 'POST', { entityType: entity, key: form.key, ...body });
    }
    setBusy(false);
    if (r.ok) { setEditing(null); loadFields(entity); } else { setMsg(r.error?.message || 'Save failed'); }
  }

  async function removeField(f) {
    if (!window.confirm(`Remove the "${f.label}" field? Existing values are kept but the field stops showing.`)) return;
    await apiSend(`/customer/field-definitions/${f.id}`, 'DELETE');
    loadFields(entity);
  }

  return (
    <div className="page-content" style={{ padding: '1rem', maxWidth: 900, margin: '0 auto' }}>
      <h2 style={{ marginTop: 0 }}>CRM Setup</h2>
      {msg && <div style={C.note}>{msg}</div>}

      {/* Industry packs */}
      <div style={C.card}>
        <div style={C.cardTitle}>Industry Packs — pick your field</div>
        <p style={C.muted}>Applying a pack instantly tailors your CRM with the right custom fields and pipeline for your trade.</p>
        {packs.length === 0 ? <div style={C.muted}>No packs available.</div> : packs.map((p) => (
          <div key={p.key} style={C.packRow}>
            <div>
              <strong>{p.label}</strong>
              <div style={C.muted}>{p.description}</div>
              <div style={C.tiny}>{p.fieldCount} fields · {p.pipelineStages} pipeline stages</div>
            </div>
            <button style={{ ...C.btnPrimary, backgroundColor: theme.accentColor }} disabled={busy} onClick={() => applyPack(p.key)}>Apply</button>
          </div>
        ))}
      </div>

      {/* Contact tags - the company's one-click suggestion list */}
      <TagSuggestionsCard theme={theme} onMessage={setMsg} />

      {/* Custom fields */}
      <div style={C.card}>
        <div style={C.cardTitle}>Custom Fields — add anything, to any record</div>
        <div style={C.tabs}>
          {ENTITY_TYPES.map((et) => (
            <button key={et} onClick={() => setEntity(et)} style={{ ...C.tab, ...(entity === et ? C.tabActive : {}) }}>{et}</button>
          ))}
        </div>
        <div style={{ textAlign: 'right', margin: '0.5rem 0' }}>
          <button style={{ ...C.btnPrimary, backgroundColor: theme.accentColor }} onClick={() => setEditing({})}>+ Add Field</button>
        </div>
        {loading ? <div style={C.muted}>Loading…</div>
          : fields.length === 0 ? <div style={C.muted}>No custom fields on {entity} yet. Add one, or apply an industry pack.</div>
          : (
            <table style={C.table}>
              <thead><tr><th style={C.th}>Label</th><th style={C.th}>Key</th><th style={C.th}>Type</th><th style={C.th}>Req</th><th style={C.th}></th></tr></thead>
              <tbody>
                {fields.map((f) => (
                  <tr key={f.id} style={C.tr}>
                    <td style={C.td}>{f.label}{f.isSystem && <span style={C.sys}>pack</span>}</td>
                    <td style={C.tdMono}>{f.key}</td>
                    <td style={C.td}>{f.dataType}</td>
                    <td style={C.td}>{f.required ? 'Yes' : ''}</td>
                    <td style={C.tdRight}>
                      <button style={C.link} onClick={() => setEditing({ ...f, optionsText: (f.options?.values || []).join(', ') })}>Edit</button>
                      <button style={C.linkDanger} onClick={() => removeField(f)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title={editing && editing.id ? 'Edit Field' : `Add Field to ${entity}`}>
        {editing && <FieldForm entity={entity} initial={editing} busy={busy} onCancel={() => setEditing(null)} onSave={saveField} />}
      </Modal>
    </div>
  );
}

/**
 * Contact Tags - the owner-managed suggestion list.
 *
 * Every module ships its own admin panel; for tagging, this is it. What the
 * owner controls here is only the SHORTCUT list - the one-click chips staff see
 * on a customer record. Nobody is ever prevented from typing a tag that is not
 * on this list, which is the whole reason tagging gets used at all.
 *
 * The list is saved whole (order included), which is why reorder, rename and
 * delete are all just "edit the list, press Save".
 */
function TagSuggestionsCard({ theme, onMessage }) {
  const [list, setList] = useState(null);   // null = loading
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    apiGet('/customer/tag-suggestions')
      .then((rows) => setList(Array.isArray(rows) ? rows : []))
      .catch(() => setList([]));
  }, []);

  function add() {
    const t = draft.trim().replace(/\s+/g, ' ');
    setDraft('');
    if (!t) return;
    if ((list || []).some((x) => x.toLowerCase() === t.toLowerCase())) return;
    setList([...(list || []), t]);
    setDirty(true);
  }

  function remove(tag) {
    setList((l) => (l || []).filter((x) => x !== tag));
    setDirty(true);
  }

  /** Move a chip one slot left or right; the saved order is the display order. */
  function move(i, delta) {
    setList((l) => {
      const next = [...(l || [])];
      const j = i + delta;
      if (j < 0 || j >= next.length) return next;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setDirty(true);
  }

  async function save() {
    setBusy(true);
    const r = await apiSend('/customer/tag-suggestions', 'PUT', { suggestions: list || [] });
    setBusy(false);
    if (r.ok) {
      setList(r.data);
      setDirty(false);
      clearTagSuggestionCache();   // so the customer screens pick the new list up
      onMessage('Tag suggestions saved.');
    } else {
      onMessage((r.error && r.error.message) || 'Could not save tag suggestions.');
    }
  }

  return (
    <div style={C.card}>
      <div style={C.cardTitle}>Contact Tags - your one-click list</div>
      <p style={C.muted}>
        These show up as one-click chips when anyone tags a customer, contact or agent.
        Staff can always type their own tags too - this list just keeps the common ones
        spelled the same way.
      </p>

      {list === null ? <div style={C.muted}>Loading...</div> : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', margin: '0.6rem 0' }}>
            {list.length === 0 && <span style={C.muted}>No suggestions yet.</span>}
            {list.map((t, i) => (
              <span key={t} style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                fontSize: '0.78rem', padding: '3px 8px', borderRadius: 9999,
                backgroundColor: 'var(--surface-page, #F3F4F6)',
                border: '1px solid var(--color-border, #E5E7EB)',
              }}>
                <button type="button" style={C.link} title="Move left"
                  onClick={() => move(i, -1)} disabled={i === 0}>&lt;</button>
                {t}
                <button type="button" style={C.link} title="Move right"
                  onClick={() => move(i, 1)} disabled={i === list.length - 1}>&gt;</button>
                <button type="button" style={C.linkDanger} title={`Remove ${t}`}
                  onClick={() => remove(t)}>&times;</button>
              </span>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              style={{ ...C.input, flex: '1 1 200px' }}
              value={draft}
              placeholder="Add a suggestion, e.g. Snowbird"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
              aria-label="New tag suggestion"
            />
            <button type="button" style={C.btnSecondary} onClick={add}>Add</button>
            <button
              type="button"
              style={{ ...C.btnPrimary, backgroundColor: theme.accentColor, opacity: busy || !dirty ? 0.6 : 1 }}
              disabled={busy || !dirty}
              onClick={save}
            >
              {busy ? 'Saving...' : 'Save list'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function FieldForm({ entity, initial, busy, onCancel, onSave }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    id: initial.id, label: initial.label || '', key: initial.key || '',
    dataType: initial.dataType || 'text', required: !!initial.required,
    section: initial.section || '', optionsText: initial.optionsText || '',
    validation: initial.validation || {},
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  function submit(e) { e.preventDefault(); onSave(form); }
  return (
    <form onSubmit={submit} style={{ padding: '1rem' }}>
      <div style={C.row}>
        <div style={C.field}><label style={C.label}>Label *</label>
          <input style={C.input} value={form.label} onChange={(e) => set('label', e.target.value)} required autoFocus placeholder="Tank Size" /></div>
        {!isEdit && (
          <div style={C.field}><label style={C.label}>Key (machine name)</label>
            <input style={C.input} value={form.key} onChange={(e) => set('key', e.target.value)} placeholder="auto from label if blank" /></div>
        )}
      </div>
      <div style={C.row}>
        <div style={C.field}><label style={C.label}>Type</label>
          <select style={C.input} value={form.dataType} onChange={(e) => set('dataType', e.target.value)}>
            {DATA_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div style={C.field}><label style={C.label}>Section</label>
          <input style={C.input} value={form.section} onChange={(e) => set('section', e.target.value)} placeholder="Septic System" /></div>
      </div>
      {TYPES_WITH_OPTIONS.includes(form.dataType) && (
        <div style={C.field}><label style={C.label}>Options (one per line or comma-separated)</label>
          <textarea style={{ ...C.input, minHeight: 60 }} value={form.optionsText} onChange={(e) => set('optionsText', e.target.value)} placeholder={'Concrete\nFiberglass\nPlastic'} /></div>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', margin: '0.5rem 0' }}>
        <input type="checkbox" checked={form.required} onChange={(e) => set('required', e.target.checked)} /> Required
      </label>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="button" style={C.btnSecondary} onClick={onCancel}>Cancel</button>
        <button type="submit" disabled={busy} style={{ ...C.btnPrimary, opacity: busy ? 0.6 : 1 }}>{busy ? 'Saving…' : (isEdit ? 'Save' : 'Add Field')}</button>
      </div>
      {!isEdit && form.key === '' && <div style={C.tiny}>Tip: leave Key blank and we'll generate it from the label (e.g. "Tank Size" → tank_size).</div>}
    </form>
  );
}

const C = {
  note: { backgroundColor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE', padding: '0.5rem 0.75rem', borderRadius: 8, marginBottom: '1rem', fontSize: '0.85rem' },
  card: { border: '1px solid var(--color-border, #E5E7EB)', borderRadius: 10, padding: '1rem', marginBottom: '1.25rem', backgroundColor: 'var(--surface-card, #fff)' },
  cardTitle: { fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.25rem' },
  muted: { color: 'var(--text-secondary, #6B7280)', fontSize: '0.85rem' },
  tiny: { color: 'var(--text-tertiary, #9CA3AF)', fontSize: '0.72rem', marginTop: '0.25rem' },
  packRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', padding: '0.6rem 0', borderTop: '1px solid var(--color-border, #E5E7EB)' },
  tabs: { display: 'flex', gap: '0.25rem', flexWrap: 'wrap', borderBottom: '1px solid var(--color-border, #E5E7EB)' },
  tab: { padding: '0.4rem 0.8rem', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-secondary, #6B7280)', borderBottom: '2px solid transparent' },
  tabActive: { color: 'var(--text-primary, #111)', fontWeight: 600, borderBottomColor: 'var(--color-primary-600, #2563EB)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: { textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid var(--color-border, #E5E7EB)', fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary, #6B7280)' },
  tr: { borderBottom: '1px solid var(--color-border, #E5E7EB)' },
  td: { padding: '0.5rem' },
  tdMono: { padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-secondary, #6B7280)' },
  tdRight: { padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' },
  sys: { marginLeft: '0.4rem', fontSize: '0.6rem', padding: '1px 5px', borderRadius: 4, backgroundColor: 'var(--surface-page, #F3F4F6)', color: 'var(--text-secondary, #6B7280)', textTransform: 'uppercase' },
  link: { background: 'none', border: 'none', color: 'var(--color-primary-600, #2563EB)', cursor: 'pointer', fontSize: '0.8rem', marginLeft: '0.5rem' },
  linkDanger: { background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: '0.8rem', marginLeft: '0.5rem' },
  row: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  field: { flex: 1, minWidth: 140, marginBottom: '0.5rem' },
  label: { display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem', color: 'var(--text-secondary, #6B7280)' },
  input: { width: '100%', padding: '0.5rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: 6, fontSize: '0.875rem', backgroundColor: 'var(--surface-card, #fff)', color: 'var(--text-primary, #111)', boxSizing: 'border-box' },
  btnPrimary: { padding: '0.5rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, backgroundColor: 'var(--color-primary-600, #2563EB)', color: '#fff' },
  btnSecondary: { padding: '0.5rem 1rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: 6, backgroundColor: 'var(--surface-card, #fff)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 },
};

export default CrmSettingsPage;
